/**
 * QuestionAdminList.js
 * Container component for managing and displaying questions in quiz administration
 * Handles the questions list, statistics display, and Add Question functionality
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { VscExpandAll, VscCollapseAll } from 'react-icons/vsc';
import { SiMicrosoftexcel } from 'react-icons/si';
import { FaUpload } from 'react-icons/fa';

import {
  Box,
  Flex,
  Text,
  Button,
  Spacer,
  Input,
  useColorModeValue,
} from '@chakra-ui/react';

import { DndContext, closestCorners } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import PassingMarkWarning from 'components/common/quiz/PassingMarkWarning';
import { UPDATE_MODE } from 'utils/constants/operationMode';
import ImportHelperMd from 'components/subQuest/quizzes/ImportHelperMd';
import QuestionAdminItem from './QuestionAdminItem';

/**
 * QuestionAdminList component
 * @param {Array} questions - Array of question objects
 * @param {Object} formInputs - Form inputs containing scoreType and passingScore
 * @param {string} mode - CREATE_MODE or UPDATE_MODE
 * @param {Function} onAddQuestion - Handler for adding new questions
 * @param {Function} onUpdateQuestion - Handler for updating questions
 * @param {Function} onRemoveQuestion - Handler for removing questions
 * @param {Function} onQuestionTypeChange - Handler for question type changes
 * @param {Function} onToggleCollapseExpand - Handler for toggling collapse/expand all questions
 * @param {boolean} allQuestionsCollapsed - Current state of all questions (collapsed or expanded)
 * @param {boolean} forceCollapseAll - External trigger to collapse all questions
 * @param {boolean} forceExpandAll - External trigger to expand all questions
 * @param {Function} onQuestionsReorder - Handler for reordering questions after drag-and-drop
 * @param {Function} onExportQuestions - Handler for exporting questions to CSV
 * @param {Function} onImportQuestions - Handler for importing questions from CSV
 */
function QuestionAdminList({
  questions,
  formInputs,
  mode,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
  onQuestionTypeChange,
  onToggleCollapseExpand,
  allQuestionsCollapsed,
  forceCollapseAll,
  forceExpandAll,
  onQuestionsReorder,
  onExportQuestions,
  onImportQuestions,
}) {
  const stickyBg = useColorModeValue('white', 'gray.900');

  // State for instruction modal
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [instructionActionType, setInstructionActionType] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && onImportQuestions) {
      onImportQuestions(file);
      event.target.value = '';
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Exit early if dropped in the same position
    if (!over || active.id === over.id) return;

    // Find the original and new positions based on question IDs
    const originalPos = questions.findIndex((q) => q.tempId === active.id);
    const newPos = questions.findIndex((q) => q.tempId === over.id);

    // Guard against invalid indexes
    if (originalPos === -1 || newPos === -1) return;

    // Reorder the questions array
    const reorderedQuestions = arrayMove(questions, originalPos, newPos);

    // Update questions with new positions
    const updatedQuestions = reorderedQuestions.map((question, index) => ({
      ...question,
      position: index,
    }));

    // Call the parent handler with reordered questions
    onQuestionsReorder(updatedQuestions);
  };

  const handleImportClick = () => {
    setInstructionActionType('import');
    setShowInstructionModal(true);
  };

  const handleExportClick = () => {
    setInstructionActionType('export');
    setShowInstructionModal(true);
  };

  const handleInstructionProceed = () => {
    setShowInstructionModal(false);

    // Trigger the appropriate action
    if (instructionActionType === 'import') {
      document.getElementById('csv-file-input').click();
    } else if (instructionActionType === 'export') {
      if (onExportQuestions) {
        onExportQuestions();
      }
    }

    setInstructionActionType(null);
  };

  const handleInstructionClose = () => {
    setShowInstructionModal(false);
    setInstructionActionType(null);
  };

  return (
    <>
      <Box
        p={4}
        position="sticky"
        top="0"
        zIndex={10}
        bg={stickyBg}
        boxShadow="sm"
      >
        {/* Warning when passing mark exceeds total marks for Actual Score */}
        <PassingMarkWarning
          scoreType={formInputs.scoreType}
          passingScore={formInputs.passingScore}
          questions={questions}
        />
        <Flex align="center">
          <Box>
            <Text>
              Total Questions:&nbsp;
              <strong>{questions.length}</strong>
            </Text>
            <Text>
              Total Mark:&nbsp;
              <strong>
                {questions.reduce((accumulator, currentObject) => {
                  return accumulator + (parseFloat(currentObject.mark) || 0);
                }, 0)}
              </strong>
            </Text>
            <Text>
              Passing Mark:&nbsp;
              <strong>
                {formInputs.passingScore}
                {formInputs.scoreType === 'Actual Score' ? '' : '%'}
              </strong>
            </Text>
          </Box>
          <Spacer />

          {/* Hidden file input for CSV import */}
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="csv-file-input"
          />

          {/* Import Questions Button */}
          <Button
            variant="outline"
            colorScheme="green"
            onClick={handleImportClick}
            mr={2}
            leftIcon={<FaUpload />}
          >
            Import Questions
          </Button>

          {questions.length > 0 &&
            onExportQuestions &&
            mode === UPDATE_MODE && (
              <Button
                variant="outline"
                colorScheme="blue"
                onClick={handleExportClick}
                mr={2}
                leftIcon={<SiMicrosoftexcel />}
              >
                Export Questions
              </Button>
            )}

          {questions.length > 0 && (
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={onToggleCollapseExpand}
              mr={2}
              leftIcon={
                allQuestionsCollapsed ? <VscExpandAll /> : <VscCollapseAll />
              }
            >
              {allQuestionsCollapsed ? 'Expand All' : 'Collapse All'}
            </Button>
          )}
          <Button variant="outline" colorScheme="blue" onClick={onAddQuestion}>
            Add Question
          </Button>
        </Flex>
      </Box>
      {/* Questions List */}
      <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
        <SortableContext
          items={questions.map((q) => q.tempId)}
          strategy={verticalListSortingStrategy}
        >
          {questions.map((question, index) => (
            <QuestionAdminItem
              key={question.tempId}
              question={question}
              index={index}
              mode={mode}
              onQuestionTypeChange={onQuestionTypeChange}
              onRemove={onRemoveQuestion}
              onUpdate={onUpdateQuestion}
              forceCollapse={forceCollapseAll}
              forceExpand={forceExpandAll}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Import/Export Instruction Modal */}
      <ImportHelperMd
        isOpen={showInstructionModal}
        onClose={handleInstructionClose}
        onProceed={handleInstructionProceed}
        actionType={instructionActionType}
      />
    </>
  );
}

QuestionAdminList.defaultProps = {
  onToggleCollapseExpand: () => {},
  allQuestionsCollapsed: false,
  forceCollapseAll: false,
  forceExpandAll: false,
  onExportQuestions: () => {},
  onImportQuestions: () => {},
};

QuestionAdminList.propTypes = {
  questions: PropTypes.array.isRequired,
  formInputs: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
  onAddQuestion: PropTypes.func.isRequired,
  onUpdateQuestion: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  onQuestionTypeChange: PropTypes.func.isRequired,
  onToggleCollapseExpand: PropTypes.func,
  allQuestionsCollapsed: PropTypes.bool,
  onQuestionsReorder: PropTypes.func.isRequired,
  forceCollapseAll: PropTypes.bool,
  forceExpandAll: PropTypes.bool,
  onExportQuestions: PropTypes.func,
  onImportQuestions: PropTypes.func,
};

export default QuestionAdminList;
